// src/tabs/logements/LivretModuleEditor.jsx — Phase A.7.1b (ADR-120)
//                                              Sprint A.9.2 PR-A92-9 (ADR-137)
//
// Modal CRUD module livret. Toggle FR | EN | ES + traduction auto Claude Haiku.
// 2 modes :
//   - Création (module=null) : titre vide, content vide, category = prop
//   - Édition (module=!=null) : pré-remplissage des champs
//
// Validation côté front (mirroring api-validation) :
//   - Titre FR obligatoire (non-vide après trim)
//   - Si category=alentours → subcategory obligatoire
//   - Si video_url renseigné → regex YouTube/Vimeo client (F-CRIT-1)
//
// Phase A.7.1b (ADR-120) :
//   - content_type 'markdown' | 'html' toggle
//   - WYSIWYG TipTap lazy-loaded si content_type='html' (chunk hors main)
//   - video_url champ optionnel (regex YT/Vimeo client + preview embed sandbox)
//
// Sprint A.9.2 PR-A92-9 (ADR-137) :
//   - Toggle 3 langues FR | EN | ES (ajout ES)
//   - Boutons "🌐 Traduire FR→EN" + "🌐 Traduire FR→ES" + "🌐 FR→toutes"
//   - Appel POST /api/beds24-mapping?action=livret_translate (Claude Haiku 4.5)
//   - Badge "🔄 Traduit auto" si user n'a pas re-modifié la traduction

import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { DS } from "../../lib/ui";
import { Lock, Save, AlertTriangle } from "lucide-react";
import { Inp, Textarea, lblStyle } from "./atoms";
import { autoTranslateEmptyLangs } from "../../lib/livret-autotranslate";
import {
  LIVRET_CATEGORY_LABELS,
  LIVRET_CATEGORY_ORDER,
  LIVRET_ALENTOURS_SUBCATEGORY_LABELS,
} from "../../lib/livret-editor-utils";
import { validateVideoUrl, videoUrlToEmbed } from "../../lib/livret-video-client";
import LivretModulePhotoCover from "./LivretModulePhotoCover";

// Phase A.7.1b lazy-load chunk WYSIWYG (TipTap ~150 kB gzip, hors bundle main)
const TipTapEditor = lazy(() =>
  import("../../components/admin/TipTapEditor.jsx"),
);

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
};

const modalStyle = {
  background: DS.card,
  borderRadius: 14,
  boxShadow: "0 8px 32px rgba(0,0,0,.2)",
  maxWidth: 720,
  width: "100%",
  maxHeight: "90vh",
  overflowY: "auto",
  padding: 24,
  fontFamily: DS.fontBody,
};

const headerStyle = {
  fontFamily: DS.fontDisplay,
  fontSize: 22,
  fontWeight: 600,
  color: DS.marineDark,
  marginBottom: 8,
};

const subtitleStyle = {
  fontSize: 12,
  color: DS.textMuted,
  marginBottom: 18,
};

const langToggleStyle = (active) => ({
  flex: 1,
  padding: "8px 14px",
  border: "none",
  borderRadius: 7,
  background: active ? DS.marine : "transparent",
  color: active ? "#fff" : DS.textSecondary,
  fontWeight: active ? 700 : 600,
  fontSize: 12,
  cursor: "pointer",
  fontFamily: DS.fontBody,
});

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  marginTop: 20,
  paddingTop: 14,
  borderTop: `1px solid ${DS.border}`,
};

const btnPrimary = (disabled) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "10px 20px",
  background: disabled ? "#cbd5e1" : DS.corail,
  color: "#fff",
  border: "none",
  borderRadius: DS.radiusControl,
  fontWeight: 600,
  fontSize: 13,
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: DS.fontBody,
});

const btnSecondary = {
  padding: "10px 20px",
  background: DS.card,
  color: DS.textSecondary,
  border: `0.5px solid ${DS.borderHover}`,
  borderRadius: DS.radiusControl,
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
  fontFamily: DS.fontBody,
};

// L6 — translateBtnStyle + translatedBadgeStyle retirés (boutons « Traduire » +
// badge « Traduit auto » supprimés : traduction auto AU SAVE des langues vides).

const SUBCATEGORIES = Object.keys(LIVRET_ALENTOURS_SUBCATEGORY_LABELS);

export default function LivretModuleEditor({
  module,
  category: defaultCategory,
  logement_id,
  onSave,
  onClose,
  currentPhoto,
  lang,
  onLangChange,
}) {
  // Mode : module existant = édition, null = création
  const isEdit = !!module;

  // State champs
  const [titleFr, setTitleFr] = useState(module?.title?.fr || "");
  const [titleEn, setTitleEn] = useState(module?.title?.en || "");
  const [titleEs, setTitleEs] = useState(module?.title?.es || "");
  const [contentFr, setContentFr] = useState(module?.content?.fr || "");
  const [contentEn, setContentEn] = useState(module?.content?.en || "");
  const [contentEs, setContentEs] = useState(module?.content?.es || "");
  const [category, setCategory] = useState(
    module?.category || defaultCategory || "informations",
  );
  const [subcategory, setSubcategory] = useState(
    module?.subcategory || "culture",
  );
  const [icon, setIcon] = useState(module?.icon || "");
  const [enabled, setEnabled] = useState(module?.enabled !== false);
  // L1b — langue pilotée par le parent (état unique partagé barre d'actions ↔
  // modal) si `lang` fourni ; sinon fallback local (modal rendu seul, tests).
  // Le reste du composant lit `langTab` / appelle `setLangTab` à l'identique :
  // la logique auto-traduction + badge « traduit auto » est intouchée.
  const isLangControlled = lang !== undefined;
  const [localLang, setLocalLang] = useState(lang || "fr");
  const langTab = isLangControlled ? lang : localLang;
  const setLangTab = isLangControlled ? onLangChange || (() => {}) : setLocalLang;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ADR-172 — conditions de visibilité (gating). Init depuis module ou defaults.
  const _vc = module?.visibility_conditions || {};
  const _tba = _vc.time_before_arrival || {};
  const [vcTimeEnabled, setVcTimeEnabled] = useState(_tba.enabled === true);
  const [vcDays, setVcDays] = useState(
    Number.isInteger(_tba.days) ? _tba.days : 1,
  );
  const [vcHour, setVcHour] = useState(
    typeof _tba.hour === "string" ? _tba.hour : "14:00",
  );
  const [vcPayment, setVcPayment] = useState(_vc.payment_required === true);
  const [vcCaution, setVcCaution] = useState(_vc.caution_required === true);
  const [vcPhoneCode, setVcPhoneCode] = useState(_vc.phone_code_required === true);
  const [vcHiddenAfter, setVcHiddenAfter] = useState(
    _vc.hidden_after_checkout === true,
  );

  // Phase A.7.1b : content_type + video_url (photo_urls JSONB future A.7.2)
  const [contentType, setContentType] = useState(
    module?.content_type === "html" ? "html" : "markdown",
  );
  const [videoUrl, setVideoUrl] = useState(module?.video_url || "");

  // Validation video_url client (regex strict, defense-in-depth)
  const videoValidation =
    videoUrl.trim().length > 0 ? validateVideoUrl(videoUrl.trim()) : null;
  const videoEmbed =
    videoValidation && videoValidation.ok ? videoUrlToEmbed(videoUrl.trim()) : null;

  // Focus titre FR au mount (UX)
  const titleRef = useRef(null);
  useEffect(() => {
    if (titleRef.current && !isEdit) {
      titleRef.current.focus();
    }
  }, [isEdit]);

  // Validation : titre FR obligatoire (bloquant)
  const isTitleFrValid = titleFr.trim().length > 0;
  // Subcategory requise si category=alentours
  const needsSubcat = category === "alentours";
  const isSubcatValid =
    !needsSubcat || (subcategory && SUBCATEGORIES.includes(subcategory));
  // video_url : si renseigné, doit être YT/Vimeo valide (sinon bloquant)
  const isVideoValid =
    videoUrl.trim().length === 0 || (videoValidation && videoValidation.ok);
  const canSave = isTitleFrValid && isSubcatValid && isVideoValid && !saving;

  // Esc pour fermer (B-2 single source overlay/Esc — convention FBZ)
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape" && !saving) {
        onClose?.();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget && !saving) {
      onClose?.();
    }
  }

  async function handleSubmit() {
    if (!canSave) return;
    setSaving(true);
    setError(null);

    // L6 — auto-traduction AU SAVE : remplit EN/ES VIDES depuis le FR (titre +
    // contenu). Jamais d'écrasement d'une saisie/correction manuelle (cible non
    // vide intacte), jamais bloquant (fallback Claude → cible laissée vide).
    const [trTitle, trContent] = await Promise.all([
      autoTranslateEmptyLangs({
        fr: titleFr,
        en: titleEn,
        es: titleEs,
        context: "title",
      }),
      autoTranslateEmptyLangs({
        fr: contentFr,
        en: contentEn,
        es: contentEs,
        context: "content",
      }),
    ]);
    // Refléter dans l'UI (cohérence immédiate).
    if (trTitle.en !== titleEn.trim()) setTitleEn(trTitle.en);
    if (trTitle.es !== titleEs.trim()) setTitleEs(trTitle.es);
    if (trContent.en !== contentEn.trim()) setContentEn(trContent.en);
    if (trContent.es !== contentEs.trim()) setContentEs(trContent.es);

    // Build payload : title/content JSONB avec FR + EN + ES si non vide.
    const title = { fr: titleFr.trim() };
    if (trTitle.en) title.en = trTitle.en;
    if (trTitle.es) title.es = trTitle.es;
    const content = {};
    if (contentFr.trim()) content.fr = contentFr.trim();
    if (trContent.en) content.en = trContent.en;
    if (trContent.es) content.es = trContent.es;

    const payload = {
      logement_id,
      category,
      subcategory: needsSubcat ? subcategory : null,
      title,
      content,
      icon: icon.trim() || null,
      enabled,
      content_type: contentType,
      // video_url : null si vide pour clear champ DB explicit
      video_url: videoUrl.trim() || null,
      // ADR-172 — conditions de visibilité (gating modules)
      visibility_conditions: {
        time_before_arrival: vcTimeEnabled
          ? { enabled: true, days: vcDays, hour: vcHour }
          : { enabled: false },
        payment_required: vcPayment,
        caution_required: vcCaution,
        phone_code_required: vcPhoneCode,
        hidden_after_checkout: vcHiddenAfter,
      },
    };

    try {
      await onSave(payload, isEdit ? module.id : null);
      onClose?.();
    } catch (e) {
      setError(e.message || "Erreur enregistrement");
      setSaving(false);
    }
  }

  // Sprint A.9.2 PR-A92-9 (ADR-137) : 3 langues FR | EN | ES
  const currentTitle =
    langTab === "fr" ? titleFr : langTab === "en" ? titleEn : titleEs;
  const currentTitleSetter =
    langTab === "fr" ? setTitleFr : langTab === "en" ? setTitleEn : setTitleEs;
  const currentContent =
    langTab === "fr" ? contentFr : langTab === "en" ? contentEn : contentEs;
  const currentContentSetter =
    langTab === "fr"
      ? setContentFr
      : langTab === "en"
        ? setContentEn
        : setContentEs;

  // L6 — setters simples. Le badge « Traduit auto » et les boutons « Traduire »
  // manuels ont été retirés : la traduction se fait AU SAVE pour les langues
  // VIDES uniquement (cf. handleSubmit + lib/livret-autotranslate), ce qui
  // garantit qu'une saisie/correction n'est jamais écrasée — sans tracker
  // auto-vs-manuel.
  function handleTitleChange(value) {
    currentTitleSetter(value);
  }
  function handleContentChange(value) {
    currentContentSetter(value);
  }

  return (
    <div
      style={overlayStyle}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? "Modifier le module" : "Nouveau module"}
    >
      <div style={modalStyle} className="fbz-modal-sheet" onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          {isEdit ? "Modifier le module" : "Nouveau module"}
        </div>
        <div style={subtitleStyle}>
          {isEdit
            ? `Édition module ID ${(module?.id || "").slice(0, 8)}…`
            : "Création d'un nouveau module dans le livret voyageur"}
        </div>

        {/* Toggle FR | EN | ES — Sprint A.9.2 PR-A92-9 (ADR-137) */}
        <div
          style={{
            display: "flex",
            gap: 4,
            background: DS.card,
            borderRadius: 8,
            padding: 4,
            marginBottom: 10,
            border: `0.5px solid ${DS.border}`,
          }}
        >
          <button
            type="button"
            onClick={() => setLangTab("fr")}
            style={langToggleStyle(langTab === "fr")}
          >
            FR (obligatoire)
          </button>
          <button
            type="button"
            onClick={() => setLangTab("en")}
            style={langToggleStyle(langTab === "en")}
          >
            EN (optionnel)
          </button>
          <button
            type="button"
            onClick={() => setLangTab("es")}
            style={langToggleStyle(langTab === "es")}
          >
            ES (optionnel)
          </button>
        </div>

        {/* L6 — boutons « Traduire » manuels RETIRÉS. La traduction FR → EN/ES
            se fait automatiquement AU SAVE pour les langues VIDES uniquement
            (jamais d'écrasement d'une correction manuelle). */}
        {langTab !== "fr" && (
          <div
            style={{
              fontSize: 11,
              color: "#6b7a90",
              fontStyle: "italic",
              marginBottom: 16,
            }}
          >
            Laissé vide → traduit automatiquement depuis le FR à l'enregistrement.
          </div>
        )}

        {/* Titre (langue active) */}
        <div style={{ marginBottom: 14 }}>
          <div style={lblStyle}>
            Titre{" "}
            {langTab === "fr"
              ? "(FR, obligatoire)"
              : langTab === "en"
                ? "(EN, optionnel)"
                : "(ES, optionnel)"}
          </div>
          <input
            ref={langTab === "fr" ? titleRef : null}
            value={currentTitle}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder={
              langTab === "fr"
                ? "ex : Instructions d'arrivée"
                : langTab === "en"
                  ? "ex : Arrival instructions"
                  : "ex : Instrucciones de llegada"
            }
            disabled={saving}
            style={{
              border: `1px solid ${
                langTab === "fr" && !isTitleFrValid ? DS.red : "#dde3ec"
              }`,
              borderRadius: 7,
              padding: "9px 12px",
              fontSize: 13,
              outline: "none",
              background: "#f8fafd",
              fontFamily: DS.fontBody,
              width: "100%",
            }}
          />
          {langTab === "fr" && !isTitleFrValid && (
            <div
              style={{
                fontSize: 11,
                color: DS.red,
                marginTop: 4,
              }}
            >
              Le titre FR est obligatoire (au moins 1 caractère)
            </div>
          )}
        </div>

        {/* Phase A.7.1b : Toggle content_type markdown | HTML WYSIWYG */}
        <div style={{ marginBottom: 10 }}>
          <div style={lblStyle}>Type de contenu</div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              onClick={() => setContentType("markdown")}
              disabled={saving}
              style={{
                padding: "6px 12px",
                background: contentType === "markdown" ? DS.navy : "#fff",
                color: contentType === "markdown" ? "#fff" : "#666",
                border: `1px solid ${
                  contentType === "markdown" ? DS.navy : "#dde3ec"
                }`,
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              Markdown
            </button>
            <button
              type="button"
              onClick={() => setContentType("html")}
              disabled={saving}
              style={{
                padding: "6px 12px",
                background: contentType === "html" ? DS.navy : "#fff",
                color: contentType === "html" ? "#fff" : "#666",
                border: `1px solid ${
                  contentType === "html" ? DS.navy : "#dde3ec"
                }`,
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              HTML (WYSIWYG)
            </button>
          </div>
        </div>

        {/* Contenu (langue active) — markdown OU WYSIWYG selon content_type */}
        <div style={{ marginBottom: 14 }}>
          <div style={lblStyle}>
            Contenu{" "}
            {langTab === "fr" ? "(FR)" : langTab === "en" ? "(EN)" : "(ES)"}
            {contentType === "markdown"
              ? " — markdown supporté (**gras**, *italique*, [lien](url))"
              : " — éditeur WYSIWYG (sanitisé serveur+client)"}
          </div>
          {contentType === "markdown" ? (
            <Textarea
              value={currentContent}
              onChange={handleContentChange}
              placeholder={
                langTab === "fr"
                  ? "ex : Sonnez à l'interphone **FBZ**...\n\nLe code de la boîte à clés est `1234`."
                  : langTab === "en"
                    ? "ex : Ring the **FBZ** intercom..."
                    : "ex : Llame al **interfono FBZ**..."
              }
              rows={8}
            />
          ) : (
            <Suspense
              fallback={
                <div
                  style={{
                    padding: 12,
                    border: "1px solid #dde3ec",
                    borderRadius: 7,
                    background: "#f8fafd",
                    color: "#888",
                    fontSize: 12,
                  }}
                >
                  Chargement de l'éditeur WYSIWYG…
                </div>
              }
            >
              <TipTapEditor
                value={currentContent}
                onChange={handleContentChange}
                disabled={saving}
              />
            </Suspense>
          )}
        </div>

        {/* Sprint A.9.1 PR-A91-2 (ADR-128) : photo couverture module.
            Option B architect : uniquement en mode édition (module.id existant).
            Création = double interaction (créer module puis Edit pour photo). */}
        {isEdit && module?.id && (
          <LivretModulePhotoCover
            module_id={module.id}
            logement_id={logement_id}
            currentPhoto={currentPhoto}
            onPhotoChange={() => {
              /* parent re-fetch via loadData async; nothing to do here */
            }}
            disabled={saving}
          />
        )}

        {/* Phase A.7.1b : champ vidéo optionnel (YouTube/Vimeo) */}
        <div style={{ marginBottom: 14 }}>
          <div style={lblStyle}>
            Vidéo (URL YouTube ou Vimeo, optionnel)
          </div>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="ex : https://youtu.be/dQw4w9WgXcQ ou https://vimeo.com/123456789"
            disabled={saving}
            style={{
              border: `1px solid ${
                !isVideoValid ? DS.red : "#dde3ec"
              }`,
              borderRadius: 7,
              padding: "9px 12px",
              fontSize: 13,
              outline: "none",
              background: "#f8fafd",
              fontFamily: DS.fontBody,
              width: "100%",
            }}
          />
          {!isVideoValid && videoValidation && (
            <div style={{ fontSize: 11, color: DS.red, marginTop: 4 }}>
              {videoValidation.message}
            </div>
          )}
          {videoEmbed && (
            <div
              style={{
                marginTop: 8,
                position: "relative",
                paddingBottom: "56.25%",
                height: 0,
                overflow: "hidden",
                borderRadius: 8,
                background: "#000",
              }}
            >
              <iframe
                title="Aperçu vidéo"
                src={videoEmbed}
                sandbox="allow-scripts allow-same-origin allow-presentation"
                loading="lazy"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            </div>
          )}
        </div>

        {/* Catégorie + sous-catégorie */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: needsSubcat ? "1fr 1fr" : "1fr",
            gap: 12,
            marginBottom: 14,
          }}
        >
          <div>
            <div style={lblStyle}>Catégorie</div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={saving}
              style={{
                border: "1px solid #dde3ec",
                borderRadius: 7,
                padding: "9px 12px",
                fontSize: 13,
                background: "#f8fafd",
                fontFamily: DS.fontBody,
                width: "100%",
                cursor: "pointer",
              }}
            >
              {LIVRET_CATEGORY_ORDER.map((cat) => (
                <option key={cat} value={cat}>
                  {LIVRET_CATEGORY_LABELS[cat]}
                </option>
              ))}
            </select>
          </div>
          {needsSubcat && (
            <div>
              <div style={lblStyle}>Sous-catégorie (alentours)</div>
              <select
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                disabled={saving}
                style={{
                  border: "1px solid #dde3ec",
                  borderRadius: 7,
                  padding: "9px 12px",
                  fontSize: 13,
                  background: "#f8fafd",
                  fontFamily: DS.fontBody,
                  width: "100%",
                  cursor: "pointer",
                }}
              >
                {SUBCATEGORIES.map((sc) => (
                  <option key={sc} value={sc}>
                    {LIVRET_ALENTOURS_SUBCATEGORY_LABELS[sc]}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Icône + enabled */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: 14,
          }}
        >
          <div>
            <div style={lblStyle}>Icône (emoji, optionnel)</div>
            <Inp
              value={icon}
              onChange={setIcon}
              placeholder="ex : 📋, 🔌, 🌿"
            />
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
              marginTop: 24,
              fontSize: 13,
              color: "#444",
            }}
          >
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={saving}
              style={{
                width: 18,
                height: 18,
                accentColor: DS.navy,
                cursor: "pointer",
              }}
            />
            Module activé (visible voyageur)
          </label>
        </div>

        {/* ADR-172 — Conditions de visibilité (gating modules) */}
        <details
          style={{
            marginBottom: 14,
            padding: "10px 12px",
            background: "#f8fafd",
            borderRadius: 10,
            border: `1px solid ${DS.border}`,
          }}
        >
          <summary
            style={{
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
              color: DS.marineDark,
              fontFamily: DS.fontBody,
              padding: "4px 0",
            }}
          >
            <Lock size={13} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            Conditions de visibilité
          </summary>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Condition 1 — date avant arrivée */}
            <div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 44, fontSize: 13, color: "#333", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={vcTimeEnabled}
                  onChange={(e) => setVcTimeEnabled(e.target.checked)}
                  disabled={saving}
                  style={{ width: 18, height: 18, accentColor: DS.navy, cursor: "pointer" }}
                />
                Visible avant l'arrivée
              </label>
              {vcTimeEnabled && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 26, marginTop: 4, flexWrap: "wrap" }}>
                  <input
                    type="number"
                    min={0}
                    max={365}
                    value={vcDays}
                    onChange={(e) => setVcDays(Math.max(0, Math.min(365, Number(e.target.value) || 0)))}
                    disabled={saving}
                    style={{ width: 64, padding: "6px 8px", border: `1px solid ${DS.border}`, borderRadius: 6, fontSize: 13 }}
                    aria-label="Nombre de jours avant l'arrivée"
                  />
                  <span style={{ fontSize: 13, color: "#666" }}>jour(s) avant l'arrivée, à</span>
                  <input
                    type="time"
                    value={vcHour}
                    onChange={(e) => setVcHour(e.target.value)}
                    disabled={saving}
                    style={{ padding: "6px 8px", border: `1px solid ${DS.border}`, borderRadius: 6, fontSize: 13 }}
                    aria-label="Heure de déverrouillage (format 24h)"
                  />
                  <span style={{ fontSize: 11, color: "#999" }}>(format 24h, ex: 14:00)</span>
                </div>
              )}
            </div>

            {/* Condition 2 — paiement */}
            <label style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 44, fontSize: 13, color: "#333", cursor: "pointer" }}>
              <input type="checkbox" checked={vcPayment} onChange={(e) => setVcPayment(e.target.checked)} disabled={saving} style={{ width: 18, height: 18, accentColor: DS.navy, cursor: "pointer" }} />
              Visible seulement après paiement OK
              <span style={{ fontSize: 11, color: "#999" }}>(Airbnb/Booking : satisfait d'office)</span>
            </label>

            {/* Condition 3 — caution */}
            <label style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 44, fontSize: 13, color: "#333", cursor: "pointer" }}>
              <input type="checkbox" checked={vcCaution} onChange={(e) => setVcCaution(e.target.checked)} disabled={saving} style={{ width: 18, height: 18, accentColor: DS.navy, cursor: "pointer" }} />
              Visible seulement après caution OK
              <span style={{ fontSize: 11, color: "#999" }}>(Airbnb : satisfait d'office)</span>
            </label>

            {/* Condition 4 — code téléphone */}
            <div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 44, fontSize: 13, color: "#333", cursor: "pointer" }}>
                <input type="checkbox" checked={vcPhoneCode} onChange={(e) => setVcPhoneCode(e.target.checked)} disabled={saving} style={{ width: 18, height: 18, accentColor: DS.navy, cursor: "pointer" }} />
                Protégé par code (4 derniers chiffres du téléphone)
              </label>
              {vcPhoneCode && (
                <p style={{ paddingLeft: 26, margin: "2px 0 0", fontSize: 11, color: DS.warningText, lineHeight: 1.4 }}>
                  <AlertTriangle size={12} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                  Le voyageur saisit les 4 derniers chiffres de son numéro pour ouvrir le module.
                  Empêche un ancien voyageur (lien conservé) de voir les infos du voyageur actuel.
                  Si le numéro est indisponible (certaines résas Booking), le module sera accessible sans code.
                </p>
              )}
            </div>

            {/* Condition 5 — masqué après départ */}
            <label style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 44, fontSize: 13, color: "#333", cursor: "pointer" }}>
              <input type="checkbox" checked={vcHiddenAfter} onChange={(e) => setVcHiddenAfter(e.target.checked)} disabled={saving} style={{ width: 18, height: 18, accentColor: DS.navy, cursor: "pointer" }} />
              Masqué après le départ (re-verrouillé post-checkout)
            </label>

            {!vcTimeEnabled && !vcPayment && !vcCaution && !vcPhoneCode && !vcHiddenAfter && (
              <p style={{ margin: 0, fontSize: 12, color: "#888", fontStyle: "italic" }}>
                Aucune condition — module visible dès publication.
              </p>
            )}
          </div>
        </details>

        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              padding: 10,
              background: DS.dangerBg,
              border: `0.5px solid ${DS.danger}`,
              borderRadius: 8,
              color: DS.dangerText,
              fontSize: 12,
              marginBottom: 14,
            }}
          >
            <AlertTriangle size={15} strokeWidth={1.8} style={{ flex: "0 0 auto" }} /> {error}
          </div>
        )}

        <div style={footerStyle}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="fbz-press"
            style={btnSecondary}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSave}
            className="fbz-press"
            style={btnPrimary(!canSave)}
          >
            <Save size={15} strokeWidth={1.8} /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
